import { errorMessage, isNotFound } from '@/lib/errors';
import OpportunitiesService from '@/services/opportunities';
import { sortByRecent } from '@/modules/formulary/utils/opportunity';

// Writes reject on failure so the calling view can report it; reads record their
// error in state instead.

// Drop a response that lands after a newer request.
let listToken = 0;
let currentToken = 0;

// Local-store-only: the real endpoints take the user from the session.
const actorOf = (rootGetters) => rootGetters.user?.email || null;

export default {
  state: {
    opportunities: [],
    opportunitiesLoaded: false,
    opportunitiesError: null,
    currentOpportunity: null,
    currentOpportunityLoaded: false,
    currentOpportunityError: null,
    currentOpportunityNotFound: false,
  },
  mutations: {
    SET_OPPORTUNITIES(state, opportunities) {
      state.opportunities = opportunities;
    },
    SET_OPPORTUNITIES_LOADED(state, loaded) {
      state.opportunitiesLoaded = loaded;
    },
    SET_OPPORTUNITIES_ERROR(state, error) {
      state.opportunitiesError = error;
    },
    SET_CURRENT_OPPORTUNITY(state, opportunity) {
      state.currentOpportunity = opportunity;
    },
    SET_CURRENT_OPPORTUNITY_LOADED(state, loaded) {
      state.currentOpportunityLoaded = loaded;
    },
    // Stored as a message; pages check `currentOpportunityNotFound` rather than its text.
    SET_CURRENT_OPPORTUNITY_ERROR(state, error) {
      state.currentOpportunityError = error ? errorMessage(error) : null;
      state.currentOpportunityNotFound = !!error && isNotFound(error);
    },
    // Updates the open record too.
    UPSERT_OPPORTUNITY(state, opportunity) {
      const index = state.opportunities.findIndex((o) => o.id === opportunity.id);
      if (index === -1) state.opportunities = [opportunity, ...state.opportunities];
      else state.opportunities = state.opportunities.map((o, i) => (i === index ? opportunity : o));
      if (state.currentOpportunity?.id === opportunity.id) state.currentOpportunity = opportunity;
    },
    REMOVE_OPPORTUNITY(state, id) {
      state.opportunities = state.opportunities.filter((o) => o.id !== id);
      if (state.currentOpportunity?.id === id) state.currentOpportunity = null;
    },
  },
  actions: {
    loadOpportunities({ commit }) {
      const token = ++listToken;
      commit('SET_OPPORTUNITIES_LOADED', false);
      commit('SET_OPPORTUNITIES_ERROR', null);
      return OpportunitiesService.list()
        .then((response) => {
          if (listToken !== token) return;
          commit('SET_OPPORTUNITIES', response.opportunities || []);
        })
        .catch((error) => {
          if (listToken !== token) return;
          // eslint-disable-next-line no-console
          console.error('Failed to load opportunities:', error);
          commit('SET_OPPORTUNITIES', []);
          commit('SET_OPPORTUNITIES_ERROR', errorMessage(error));
        })
        .finally(() => {
          if (listToken === token) commit('SET_OPPORTUNITIES_LOADED', true);
        });
    },
    // Resolves to null when not found or superseded.
    loadOpportunity({ commit }, id) {
      const token = ++currentToken;
      commit('SET_CURRENT_OPPORTUNITY_LOADED', false);
      commit('SET_CURRENT_OPPORTUNITY_ERROR', null);
      return OpportunitiesService.get(id)
        .then((response) => {
          if (currentToken !== token) return null;
          commit('SET_CURRENT_OPPORTUNITY', response.opportunity || null);
          return response.opportunity || null;
        })
        .catch((error) => {
          if (currentToken !== token) return null;
          // eslint-disable-next-line no-console
          console.error('Failed to load opportunity:', error);
          commit('SET_CURRENT_OPPORTUNITY', null);
          commit('SET_CURRENT_OPPORTUNITY_ERROR', error);
          return null;
        })
        .finally(() => {
          if (currentToken === token) commit('SET_CURRENT_OPPORTUNITY_LOADED', true);
        });
    },
    clearCurrentOpportunity({ commit }) {
      currentToken += 1;
      commit('SET_CURRENT_OPPORTUNITY', null);
      commit('SET_CURRENT_OPPORTUNITY_LOADED', false);
      commit('SET_CURRENT_OPPORTUNITY_ERROR', null);
    },
    createOpportunity({ commit, rootGetters }, fields) {
      return OpportunitiesService.create(fields, actorOf(rootGetters)).then(({ opportunity }) => {
        commit('UPSERT_OPPORTUNITY', opportunity);
        return opportunity;
      });
    },
    // `patch` holds only the fields to change.
    updateOpportunity({ commit }, { id, patch }) {
      return OpportunitiesService.update(id, patch).then(({ opportunity }) => {
        commit('UPSERT_OPPORTUNITY', opportunity);
        return opportunity;
      });
    },
    deleteOpportunity({ commit }, id) {
      return OpportunitiesService.delete(id).then(() => {
        commit('REMOVE_OPPORTUNITY', id);
      });
    },
    addOpportunityNote({ commit, rootGetters }, { id, content }) {
      return OpportunitiesService.addNote(id, content, actorOf(rootGetters)).then(({ opportunity }) => {
        commit('UPSERT_OPPORTUNITY', opportunity);
        return opportunity;
      });
    },
  },
  getters: {
    // Sorted here too, so an upserted record lands in place.
    opportunities: (state) => sortByRecent(state.opportunities),
    opportunitiesLoaded: (state) => state.opportunitiesLoaded,
    opportunitiesError: (state) => state.opportunitiesError,
    currentOpportunity: (state) => state.currentOpportunity,
    currentOpportunityLoaded: (state) => state.currentOpportunityLoaded,
    currentOpportunityError: (state) => state.currentOpportunityError,
    currentOpportunityNotFound: (state) => state.currentOpportunityNotFound,
  },
};
